import { css, styled } from "@mui/material";
import { Eye, EyeOff, Folder } from "@procore/core-icons";
import { Box, Button, colors, spacing, Typography } from "@procore/core-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ObjectTreeLoadMoreButton } from "components/objectTree/objectTreeLoadMoreButton";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { TreeNodeRendererProps } from "features/modelTree/types";
import { renderActions, selectMainObject } from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { useTouchLongPress } from "hooks/useTouchLongPress";
import {
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    selectObjectHierarchy,
    selectObjectHierarchyFromFilteredTree,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
    unselectObjectHierarchy,
    unselectObjectHierarchyFromFilteredTree,
} from "utils/objectActions";
import { highlightText } from "utils/textHighlight";
import { getGroupStateForFilteredTree, getGroupStateForIdSet, GroupState } from "utils/treeHelpers";

import { CLICK_DELAY, ITEM_HEIGHT, ROW_PADDING_LEFT } from "../constants";
import { calculateTreeIndentation, formatNodeDisplayName, getGroupCountLabel } from "../utils/formatting";
import { TreeNodeCheckbox } from "./TreeNodeCheckbox";
import { TreeNodeChevron } from "./TreeNodeChevron";
import { VisibilityButton } from "./VisibilityButton";

const TreeNodeRow = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        padding-left: ${ROW_PADDING_LEFT}px;
        cursor: pointer;
        transition: background-color 0.15s ease;
        background-color: transparent;
        width: 100%;
        height: ${ITEM_HEIGHT}px;
        user-select: none;
        gap: ${spacing.sm}px;

        &:hover {
            background-color: ${colors.gray96};
        }
    `,
);

export function ObjectTreeNodeProcore({
    item,
    onToggleExpanded,
    onExpandGroup,
    onLoadMore,
    setLoading,
    abortSignal,
    tree,
    filteredObjectIds,
    searchTerm,
    onNodeRightClick,
}: TreeNodeRendererProps) {
    const { t } = useTranslation();
    const { node, isLoadMore, level } = item;
    const singleClickTimerRef = useRef<number | null>(null);

    const dispatch = useAppDispatch();
    const mainObject = useAppSelector(selectMainObject);
    const mainObjectRef = useRef(mainObject);
    mainObjectRef.current = mainObject;
    const {
        state: { db },
    } = useExplorerGlobals(true);

    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const highlightedIds = highlightedSet.ids;

    const hidden = useHidden();
    const hiddenIds = hidden.ids;
    const [isHovered, setIsHovered] = useState(false);

    const longPress = useTouchLongPress({
        onLongPress: ({ clientX, clientY }) => {
            if (singleClickTimerRef.current !== null) {
                window.clearTimeout(singleClickTimerRef.current);
                singleClickTimerRef.current = null;
            }
            onNodeRightClick?.(clientX, clientY, item);
        },
    });

    const selected: GroupState = useMemo(() => {
        if (filteredObjectIds != null && tree) {
            return getGroupStateForFilteredTree(highlightedIds, item, filteredObjectIds, tree);
        }
        return getGroupStateForIdSet(highlightedIds, item, tree);
    }, [item, highlightedIds, filteredObjectIds, tree]);

    const hiddenState: GroupState = useMemo(() => {
        if (filteredObjectIds != null && tree) {
            return getGroupStateForFilteredTree(hiddenIds, item, filteredObjectIds, tree);
        }
        return getGroupStateForIdSet(hiddenIds, item, tree);
    }, [item, hiddenIds, filteredObjectIds, tree]);

    // Show EyeOff only when all children are hidden
    const isHidden = hiddenState === "all";

    useEffect(() => {
        return () => {
            if (singleClickTimerRef.current) {
                window.clearTimeout(singleClickTimerRef.current);
                singleClickTimerRef.current = null;
            }
        };
    }, []);

    const handleToggleSelection = useCallback(async () => {
        if (abortSignal.aborted) {
            return;
        }

        const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

        try {
            let affectedIds: number[];
            // If we have filtered IDs, use the filtered-tree-aware selection functions
            if (filteredObjectIds != null && tree) {
                if (selected === "all") {
                    affectedIds = await unselectObjectHierarchyFromFilteredTree({
                        nodes: objects,
                        filteredObjectIds,
                        dispatchHighlighted,
                        tree,
                    });
                } else {
                    // Note: dispatchHidden is intentionally NOT passed here.
                    // Objects can exist in both selected and hidden sets simultaneously.
                    affectedIds = await selectObjectHierarchyFromFilteredTree({
                        nodes: objects,
                        filteredObjectIds,
                        dispatchHighlighted,
                        tree,
                    });
                }
            } else {
                // Original logic for non-filtered tree
                if (selected === "all") {
                    affectedIds = await unselectObjectHierarchy({
                        nodes: objects,
                        db,
                        abortSignal,
                        setLoading,
                        dispatchHighlighted,
                    });
                } else {
                    // Note: dispatchHidden is intentionally NOT passed here.
                    // Objects can exist in both selected and hidden sets simultaneously.
                    affectedIds = await selectObjectHierarchy({
                        nodes: objects,
                        db,
                        abortSignal,
                        setLoading,
                        dispatchHighlighted,
                    });
                }
            }
            if (affectedIds.length > 0) {
                const isUnselect = selected === "all";
                const currentMainObject = mainObjectRef.current;
                const clearedMainObjectId =
                    isUnselect && currentMainObject !== undefined && affectedIds.includes(currentMainObject)
                        ? currentMainObject
                        : undefined;

                if (clearedMainObjectId !== undefined) {
                    dispatch(renderActions.setMainObject(undefined));
                }

                dispatch(
                    undoRedoActions.push({
                        description: isUnselect ? "Unselect objects" : "Select objects",
                        undoCallback: () => {
                            dispatchHighlighted(
                                isUnselect ? highlightActions.add(affectedIds) : highlightActions.remove(affectedIds),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(clearedMainObjectId));
                            }
                        },
                        redoCallback: () => {
                            dispatchHighlighted(
                                isUnselect ? highlightActions.remove(affectedIds) : highlightActions.add(affectedIds),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(undefined));
                            }
                        },
                    }),
                );
            }
        } catch (error) {
            if (!abortSignal.aborted) {
                console.error("Selection failed:", error);
            }
        }
    }, [item, selected, db, abortSignal, setLoading, dispatch, dispatchHighlighted, filteredObjectIds, tree]);

    const handleRowClick = () => {
        if (longPress.wasLongPressTriggered()) {
            return;
        }

        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        singleClickTimerRef.current = window.setTimeout(() => {
            singleClickTimerRef.current = null;
            handleToggleSelection();
        }, CLICK_DELAY);
    };

    const handleRowDoubleClick = () => {
        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        if (!node.isLeaf) {
            onToggleExpanded(item);
        }
    };

    const handleToggleVisibility = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();

            const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

            try {
                let affectedIds: number[];
                // If we have filtered IDs, use the filtered-tree-aware visibility functions
                if (filteredObjectIds != null && tree) {
                    if (hiddenState === "all") {
                        affectedIds = await showObjectHierarchyFromFilteredTree({
                            nodes: objects,
                            filteredObjectIds,
                            dispatchHidden,
                            tree,
                        });
                    } else {
                        affectedIds = await hideObjectHierarchyFromFilteredTree({
                            nodes: objects,
                            filteredObjectIds,
                            dispatchHidden,
                            tree,
                        });
                    }
                } else {
                    if (hiddenState === "all") {
                        affectedIds = await showObjectHierarchy({
                            nodes: objects,
                            db,
                            abortSignal,
                            setLoading,
                            dispatchHidden,
                        });
                    } else {
                        affectedIds = await hideObjectHierarchy({
                            nodes: objects,
                            db,
                            abortSignal,
                            setLoading,
                            dispatchHidden,
                        });
                    }
                }
                if (affectedIds.length > 0) {
                    const isShow = hiddenState === "all";
                    dispatch(
                        undoRedoActions.push({
                            description: isShow ? "Show objects" : "Hide objects",
                            undoCallback: () =>
                                dispatchHidden(
                                    isShow ? hiddenActions.add(affectedIds) : hiddenActions.remove(affectedIds),
                                ),
                            redoCallback: () =>
                                dispatchHidden(
                                    isShow ? hiddenActions.remove(affectedIds) : hiddenActions.add(affectedIds),
                                ),
                        }),
                    );
                }
            } catch (error) {
                if (!abortSignal.aborted) {
                    console.error("Toggle visibility failed:", error);
                }
            }
        },
        [item, hiddenState, db, abortSignal, setLoading, dispatch, dispatchHidden, filteredObjectIds, tree],
    );

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        longPress.cancel();

        if (longPress.wasLongPressTriggered()) {
            return;
        }

        const mouseX = event.clientX;
        const mouseY = event.clientY;

        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        onNodeRightClick?.(mouseX, mouseY, item);
    };

    const levelIndent = calculateTreeIndentation(level);
    const displayName = formatNodeDisplayName(item);
    const groupCountLabel = getGroupCountLabel(item);

    if (isLoadMore) {
        return <ObjectTreeLoadMoreButton item={item} onClick={() => onLoadMore(item)} />;
    }

    const hasChildren = !node.isLeaf;
    const isExpanded = node.expanded;

    return (
        <TreeNodeRow
            data-testid="object-tree-node-row"
            {...longPress.handlers}
            onClick={handleRowClick}
            onDoubleClick={handleRowDoubleClick}
            onContextMenu={handleContextMenu}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <TreeNodeCheckbox selected={selected} onSelection={handleToggleSelection} />
            {level > 0 && <Box style={{ width: `${levelIndent}px`, flexShrink: 0 }} />}

            {hasChildren && (
                <>
                    <TreeNodeChevron isExpanded={isExpanded} onToggle={() => onToggleExpanded(item)} />
                    <Box display="flex">
                        <Folder style={{ color: colors.black, width: 16, height: 16 }} />
                    </Box>
                </>
            )}

            <Typography
                style={{
                    fontSize: "13px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {searchTerm ? highlightText(displayName, searchTerm) : displayName}
            </Typography>
            {item.nodeGroup && (
                <Button
                    variant="tertiary"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onExpandGroup(item);
                    }}
                >
                    {groupCountLabel}
                </Button>
            )}
            {node.loading && <Typography style={{ fontSize: "11px", color: colors.gray60 }}>{t("loading")}</Typography>}

            {(isHovered || isHidden) && (
                <VisibilityButton
                    data-testid="object-tree-node-visibility"
                    style={{ marginLeft: "auto" }}
                    onClick={handleToggleVisibility}
                >
                    {isHidden ? (
                        <EyeOff style={{ width: 16, height: 16 }} />
                    ) : (
                        <Eye style={{ width: 16, height: 16 }} />
                    )}
                </VisibilityButton>
            )}
        </TreeNodeRow>
    );
}
